import React, { useState, useEffect, useMemo } from "react"

const SIBLING_COUNT = 2

function getVisiblePages(currentPage, totalPages) {
  if (totalPages <= 1) return []

  const range = []

  for (let page = 1; page <= totalPages; page += 1) {
    const isBoundary = page === 1 || page === totalPages
    const isNearCurrent = page >= currentPage - SIBLING_COUNT && page <= currentPage + SIBLING_COUNT

    if (isBoundary || isNearCurrent) {
      range.push(page)
    }
  }

  const pagesWithEllipsis = []
  let previousPage = null

  range.forEach(page => {
    if (previousPage !== null) {
      if (page - previousPage === 2) {
        pagesWithEllipsis.push({ type: "page", value: previousPage + 1 })
      } else if (page - previousPage !== 1) {
        pagesWithEllipsis.push({ type: "ellipsis", key: `after-${previousPage}` })
      }
    }

    pagesWithEllipsis.push({ type: "page", value: page })
    previousPage = page
  })

  return pagesWithEllipsis
}

function Pagination({
  pageChangeHandler,
  totalRows,
  rowsPerPage,
  initialPage = 1,
}) {
  const noOfPages = Math.ceil(totalRows / rowsPerPage)
  const [currentPage, setCurrentPage] = useState(initialPage)
  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, noOfPages),
    [currentPage, noOfPages],
  )

  const canGoBack = currentPage > 1
  const canGoNext = currentPage < noOfPages

  const onNextPage = () => setCurrentPage(prev => Math.min(prev + 1, noOfPages))
  const onPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1))
  const onPageSelect = pageNo => setCurrentPage(pageNo)

  useEffect(() => {
    setCurrentPage(initialPage)
  }, [initialPage])

  useEffect(() => {
    pageChangeHandler(currentPage)
  }, [currentPage, pageChangeHandler])

  return (
    <div className="d-flex justify-content-center mt-5">
      {noOfPages > 1 ? (
        <nav aria-label="Page navigation">
          <ul className="pagination">
            <li className={`page-item${canGoBack ? "" : " disabled"}`}>
              <button
                type="button"
                className="page-link"
                onClick={onPrevPage}
                disabled={!canGoBack}
                aria-label="Previous page">
                &laquo;
              </button>
            </li>
            {visiblePages.map(item => (
              item.type === "ellipsis" ? (
                <li className="page-item disabled" key={item.key}>
                  <span className="page-link">&hellip;</span>
                </li>
              ) : (
                <li
                  className={`page-item${item.value === currentPage ? " active" : ""}`}
                  key={item.value}>
                  <button
                    type="button"
                    onClick={() => onPageSelect(item.value)}
                    className="page-link"
                    aria-current={item.value === currentPage ? "page" : undefined}>
                    {item.value}
                  </button>
                </li>
              )
            ))}
            <li className={`page-item${canGoNext ? "" : " disabled"}`}>
              <button
                type="button"
                className="page-link"
                onClick={onNextPage}
                disabled={!canGoNext}
                aria-label="Next page">
                &raquo;
              </button>
            </li>
          </ul>
        </nav>
      ) : null}
    </div>
  )
}

export default Pagination
